import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  FormGroup,
  CircularProgress,
  Alert,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  Paper
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoIcon from '@mui/icons-material/Info';
import SecurityIcon from '@mui/icons-material/Security';
import ImageIcon from '@mui/icons-material/Image';
import CollectionsIcon from '@mui/icons-material/Collections';
import CategoryIcon from '@mui/icons-material/Category';
import DescriptionIcon from '@mui/icons-material/Description';
import PeopleIcon from '@mui/icons-material/People';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import SettingsIcon from '@mui/icons-material/Settings';
import TableChartIcon from '@mui/icons-material/TableChart';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import BugReportIcon from '@mui/icons-material/BugReport';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import SpellcheckIcon from '@mui/icons-material/Spellcheck';
import { IRole, PermissionString, PermissionsConfigResponse, PermissionGroup, PermissionDefinition } from '../types/damTypes';
import { createRole, updateRole } from '../services/roleService';

interface RoleFormModalProps {
  open: boolean;
  onClose: () => void;
  role: IRole | null;
  permissionsConfig: PermissionsConfigResponse | null;
  onSave: () => void;
}

const RoleFormModal: React.FC<RoleFormModalProps> = ({ open, onClose, role, permissionsConfig, onSave }) => {
  const [formData, setFormData] = useState<{ name: string; description: string; permissions: PermissionString[]; onlyKontor?: boolean }>({
    name: '',
    description: '',
    permissions: [],
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState<string>('');

  useEffect(() => {
    if (role) {
      setFormData({
        name: role.name,
        description: role.description || '',
        permissions: role.permissions || [],
        onlyKontor: role.onlyKontor || false,
      });
    } else {
      setFormData({
        name: '',
        description: '',
        permissions: [],
        onlyKontor: false,
      });
    }
    setError(null); // Reset error when modal opens or role changes
  }, [role, open]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePermissionChange = (permission: PermissionString, checked: boolean) => {
    setFormData(prev => {
      const newPermissions = checked
        ? [...prev.permissions, permission]
        : prev.permissions.filter(p => p !== permission);
      return { ...prev, permissions: newPermissions };
    });
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);
    try {
      let response;
      const payload = { ...formData };

      if (role && role._id) {
        response = await updateRole(role._id, payload);
      } else {
        response = await createRole(payload);
      }

      if (response.data) {
        onSave();
        onClose();
      } else {
        setError(response.error || 'Ett fel inträffade vid sparande.');
      }
    } catch (err: any) {
      setError(err.message || 'Ett oväntat serverfel inträffade.');
    }
    setLoading(false);
  };

  // Helpers: friendly labels and icons
  const groupIcons: Record<string, React.ReactNode> = {
    ASSETS: <ImageIcon />,
    COLLECTIONS: <CollectionsIcon />,
    CATEGORIES: <CategoryIcon />,
    TEMPLATES: <DescriptionIcon />,
    DOWNLOAD_TEMPLATES: <DownloadIcon />,
    USERS: <PeopleIcon />,
    ROLES: <SecurityIcon />,
    API_KEYS: <VpnKeyIcon />,
    SYSTEM: <SettingsIcon />,
    ADMIN_ALL: <AdminPanelSettingsIcon />,
    CSV: <TableChartIcon />,
    BRIEFS: <DescriptionIcon />,
    BARCODE: <QrCode2Icon />,
    VALIDOO_EXPORT: <CloudDownloadIcon />,
    ISSUES: <BugReportIcon />,
    TASKS: <TaskAltIcon />,
    TRASH: <DeleteForeverIcon />,
    AI_IMAGES: <AutoAwesomeIcon />,
    RECIPE_CORRECTION: <SpellcheckIcon />,
  };

  const friendlyGroupLabels: Record<string, string> = {
    ASSETS: 'Tillgångar (Assets)',
    COLLECTIONS: 'Samlingar',
    CATEGORIES: 'Kategorier',
    TEMPLATES: 'Mallar',
    DOWNLOAD_TEMPLATES: 'Nedladdningsmallar',
    USERS: 'Användare',
    ROLES: 'Roller',
    API_KEYS: 'API-nycklar',
    SYSTEM: 'System',
    ADMIN_ALL: 'Full systemåtkomst',
    CSV: 'CSV Import/Export',
    BRIEFS: 'Briefs/beställningar',
    BARCODE: 'EAN-koder',
    VALIDOO_EXPORT: 'Validoo-export',
    ISSUES: 'Ärenden (Issues)',
    TASKS: 'Uppgifter (Tasks)',
    TRASH: 'Soptunna',
    AI_IMAGES: 'Athena AI Studio',
    PROMPTS: 'AI-prompter',
    BRAND_GUIDE: 'Varumärkesguide',
    POPUP: 'Popup-meddelanden',
    INHOUSE: 'Inhouse Timeline',
    NYHETSLANSERINGAR: 'Nyhetslanseringar',
    RECIPE_CORRECTION: 'Receptkorrigering',
  };

  const groupDescriptions: Record<string, string> = {
    ASSETS: 'Hantera tillgångar som bilder, videos och filer. Inkluderar uppladdning, redigering, borttagning, versionshantering och mer.',
    COLLECTIONS: 'Skapa och hantera samlingar för att organisera tillgångar i grupper.',
    CATEGORIES: 'Skapa och hantera kategorier för att klassificera tillgångar.',
    TEMPLATES: 'Skapa och hantera mallar för Validoo-integration och produktdata.',
    DOWNLOAD_TEMPLATES: 'Skapa och hantera nedladdningsmallar för att anpassa nedladdningsformat.',
    USERS: 'Hantera användarkonton, lösenord och rollpåtildelning.',
    ROLES: 'Skapa och redigera roller med olika behörigheter.',
    API_KEYS: 'Generera och hantera API-nycklar för extern åtkomst.',
    SYSTEM: 'Systeminställningar som backup, e-post och allmänna konfigurationer.',
    ADMIN_ALL: 'Ger full behörighet i hela systemet. När denna är vald är övriga kryss informativa och låsta.',
    CSV: 'Importera och exportera data i CSV-format.',
    BRIEFS: 'Skapa och hantera briefs/beställningar för annonsproduktion och skicka till ClickUp.',
    BARCODE: 'Ger åtkomst till kodgeneratorn för EAN-koder och QR-koder.',
    VALIDOO_EXPORT: 'Ger åtkomst till funktionen "Spara för Validoo" på produktkort och tillhörande bildexport.',
    ISSUES: 'Rapportera och följa upp buggar, fixes, features och förbättringar.',
    TASKS: 'Hantera Go-live-uppgifter och prioriteringar i kanban-board.',
    TRASH: 'Hantera soptunnan - visa, återställa och permanent radera borttagna filer.',
    AI_IMAGES: 'Generera AI-bilder med Athena AI Studio. Inkluderar användning, granskning, publicering och administration.',
    PROMPTS: 'Använda och administrera AI-promptar.',
    BRAND_GUIDE: 'Visa och redigera varumärkesguiden.',
    POPUP: 'Skapa och hantera popup-meddelanden som visas för användare.',
    INHOUSE: 'Hantera inhouse-tidslinje för intern produktion.',
    NYHETSLANSERINGAR: 'Skapa och följa nyhetslanseringar i Athena.',
    RECIPE_CORRECTION: 'Granska och rätta recept mot masterprompten, inklusive import från Word/PDF.',
  };

  const friendlyPermissionLabels: Record<string, string> = {
    CREATE: 'Skapa',
    READ: 'Läsa',
    WRITE: 'Spara lansering',
    APPROVE: 'Registrera beslut',
    UPDATE: 'Uppdatera',
    DELETE: 'Ta bort',
    DOWNLOAD: 'Ladda ner',
    MANAGE_VERSIONS: 'Hantera versioner',
    MANAGE_FAVORITES: 'Hantera favoriter',
    RUN_AI_ANALYSIS: 'Kör AI-analys',
    MANAGE_BADGES: 'Hantera märken',
    SHARE: 'Dela länkar',
    ARCHIVE: 'Arkivera',
    SORT_WORDPRESS_PUBLISHED_AT: 'Sortera recept på WordPress-datum',
    FILTER_KONTOR_PRODUCTS: 'Filtrera Produkter på Kontor',
    FILTER_KONTOR_WINE: 'Filtrera Vin på Kontor',
    ADD_ASSETS: 'Lägg till tillgångar',
    ASSIGN_ROLES: 'Tilldela roller',
    ACCESS: 'Visa och använda sidan',
    MANAGE_SETTINGS: 'Hantera inställningar',
    MANAGE_BACKUPS: 'Hantera backuper',
    MANAGE_INVENTORY: 'Hantera Lager & Order',
    MANAGE_BRANDING: 'Hantera branding, logotyp och favicon',
    MANAGE_LOGIN_PAGE: 'Hantera inloggningssida',
    MANAGE_IMAGE_TOOLS: 'Hantera bildverktyg',
    MANAGE_MAINTENANCE: 'Hantera underhållsmeddelanden',
    VIEW_ANALYTICS: 'Visa analytics',
    VIEW_FIELD_DIAGNOSTICS: 'Visa fältdiagnostik',
    MANAGE_AI_QUEUE: 'Hantera AI-kö',
    MANAGE_DABAS_SYNC: 'Hantera Dabas-synk',
    MANAGE_PRODUCT_COMPARISON: 'Hantera produktkontroll',
    RUN_PRODUCT_IMAGE_VALIDATION: 'Kör Bildkontroll Validoo',
    ADMIN_ALL: 'Full systemåtkomst',
    IMPORT: 'Importera',
    EXPORT: 'Exportera',
    MANAGE_TEMPLATES: 'Hantera mallar',
    SEND_TO_CLICKUP: 'Skicka till ClickUp',
    USE: 'Använda',
    REVIEW: 'Granska',
    PUBLISH: 'Publicera',
    ADMIN: 'Administrera',
    CALENDAR: 'Redigera årskalender',
    PRODUCT_INFO: 'Produktinformation Dabas',
    ALCOHOL_INFO: 'Produktinfo Alkohol',
    RESTORE: 'Återställa',
    PURGE: 'Permanent radera',
    MANAGE: 'Hantera',
  };

  // Select/Deselect per group
  const toggleGroup = (permissionIds: string[], selectAll: boolean) => {
    setFormData(prev => {
      const set = new Set(prev.permissions);
      if (selectAll) {
        permissionIds.forEach(id => set.add(id));
      } else {
        permissionIds.forEach(id => set.delete(id));
      }
      return { ...prev, permissions: Array.from(set) };
    });
  };

  const isAdminAllSelected = formData.permissions.includes('admin:all');

  // Räkna endast giltiga behörigheter som finns i permissionsConfig
  const getValidPermissionsCount = () => {
    if (!permissionsConfig || !permissionsConfig.groupedPermissions) return formData.permissions.length;
    
    const allValidPermissions = new Set<string>();
    Object.values(permissionsConfig.groupedPermissions).forEach((group: PermissionGroup) => {
      const perms = (group as any).permissions as PermissionDefinition[];
      perms.forEach(p => allValidPermissions.add(p.id));
    });
    
    return formData.permissions.filter(p => allValidPermissions.has(p) || p === 'admin:all').length;
  };

  // Snabbmallar för behörigheter med beskrivningar
  const templates = {
    viewer: {
      name: 'Viewer',
      description: 'Kan visa och ladda ner tillgångar, men inte redigera eller skapa något.',
      permissions: [
        'assets:read','assets:download','assets:share',
        'collections:read',
        'categories:read',
        'templates:read',
        'downloadTemplates:read'
      ] as PermissionString[]
    },
    contributor: {
      name: 'Contributor',
      description: 'Kan ladda upp och redigera tillgångar samt hantera sina egna samlingar.',
      permissions: [
        'assets:read','assets:download','assets:share','assets:create','assets:update',
        'assets:manageVersions','assets:manageFavorites','assets:manageBadges',
        'collections:read','collections:create','collections:addAssets',
        'categories:read',
        'templates:read',
        'downloadTemplates:read'
      ] as PermissionString[]
    },
    editor: {
      name: 'Editor',
      description: 'Full kontroll över tillgångar och samlingar. Kan arkivera och ta bort innehåll.',
      permissions: [
        'assets:read','assets:create','assets:update','assets:delete','assets:download',
        'assets:manageVersions','assets:manageFavorites','assets:manageBadges','assets:share',
        'assets:archive','assets:runAIAnalysis',
        'collections:read','collections:create','collections:update','collections:delete','collections:addAssets',
        'categories:read',
        'templates:read',
        'downloadTemplates:read','downloadTemplates:create','downloadTemplates:update'
      ] as PermissionString[]
    },
    manager: {
      name: 'Manager',
      description: 'Kan hantera kategorier, mallar och CSV-funktioner utöver allt en Editor kan göra.',
      permissions: [
        'assets:read','assets:create','assets:update','assets:delete','assets:download',
        'assets:manageVersions','assets:manageFavorites','assets:manageBadges','assets:share',
        'assets:archive','assets:runAIAnalysis',
        'collections:read','collections:create','collections:update','collections:delete','collections:addAssets',
        'categories:read','categories:create','categories:update','categories:delete',
        'templates:read','templates:create','templates:update','templates:delete',
        'downloadTemplates:read','downloadTemplates:create','downloadTemplates:update','downloadTemplates:delete',
        'csv:import','csv:export','csv:manageTemplates'
      ] as PermissionString[]
    },
    admin: {
      name: 'Administrator',
      description: 'Full systemåtkomst. Kan hantera användare, roller, API-nycklar och systeminställningar.',
      permissions: ['admin:all'] as PermissionString[]
    }
  };

  const applyTemplate = (template: keyof typeof templates) => {
    const perms = templates[template].permissions;
    setFormData(prev => ({ ...prev, permissions: Array.from(new Set(perms)) }));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{role ? 'Redigera Roll' : 'Skapa Ny Roll'}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {isAdminAllSelected && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Denna roll har full behörighet via <strong>admin:all</strong>. Andra kryss visas endast som information och kan inte ändras så länge full systemåtkomst är vald.
          </Alert>
        )}
        {role?.isSystemRole && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Detta är en systemroll. Behörigheter kan ändras här, och uppdateringen påverkar alla användare som har rollen.
          </Alert>
        )}

        {/* Snabbmallar */}
        <Paper elevation={0} sx={{ bgcolor: '#f5f5f5', p: 2, mb: 3, borderRadius: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <SecurityIcon fontSize="small" />
            Snabbmallar
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Välj en mall för att snabbt tilldela vanliga behörighetsgrupper. Du kan sedan justera individuella behörigheter nedan.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            {Object.entries(templates).map(([key, template]) => (
              <Tooltip 
                key={key}
                title={
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                      {template.name}
                    </Typography>
                    <Typography variant="caption">
                      {template.description}
                    </Typography>
                    <Typography variant="caption" sx={{ display: 'block', mt: 1, opacity: 0.8 }}>
                      {template.permissions.length} behörigheter
                    </Typography>
                  </Box>
                }
                arrow
                placement="top"
              >
                <Button 
                  size="medium" 
                  variant="outlined" 
                  onClick={() => applyTemplate(key as keyof typeof templates)}
                  color={key === 'admin' ? 'error' : 'primary'}
                  sx={{ textTransform: 'none' }}
                >
                  {template.name}
                </Button>
              </Tooltip>
            ))}
          </Box>
        </Paper>
        <TextField
          autoFocus
          margin="dense"
          name="name"
          label="Rollnamn"
          type="text"
          fullWidth
          variant="outlined"
          value={formData.name}
          onChange={handleChange}
          disabled={loading || (role?.isSystemRole && role.name !== 'Admin')} // Admin kan redigera sitt namn, men inte andra systemroller
          required
          sx={{ mb: 2 }}
        />
        <TextField
          margin="dense"
          name="description"
          label="Beskrivning"
          type="text"
          fullWidth
          multiline
          rows={3}
          variant="outlined"
          value={formData.description}
          onChange={handleChange}
          disabled={loading}
          sx={{ mb: 2 }}
        />
        <TextField
          margin="dense"
          label="Sök behörigheter"
          type="text"
          fullWidth
          variant="outlined"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Sök på grupp, namn eller nyckel (t.ex. csv:export)"
          sx={{ mb: 2 }}
        />
        
        {/* Minimal visibility toggle for Kontor-only roles */}
        <Box sx={{ mt: 1, mb: 2 }}>
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!formData.onlyKontor}
                  onChange={(e) => setFormData(prev => ({ ...prev, onlyKontor: e.target.checked }))}
                />
              }
              label="Bara för kontor (ser endast assets markerade “Kontor”)"
            />
          </FormGroup>
        </Box>

        <Box sx={{ mt: 3, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <SecurityIcon sx={{ color: '#072f53' }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>Behörigheter</Typography>
            <Chip 
              label={`${getValidPermissionsCount()} valda`} 
              size="small" 
              color="primary" 
              sx={{ ml: 1 }}
            />
          </Box>
          <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 2 }}>
            Välj vilka funktioner denna roll ska ha tillgång till. Använd snabbmallarna ovan för att snabbt sätta vanliga behörighetsnivåer.
          </Alert>
        </Box>

        {(!permissionsConfig || !permissionsConfig.groupedPermissions) && !loading ? (
          <Typography>Laddar behörighetskonfiguration...</Typography>
        ) : (
          <Box>
            {permissionsConfig && permissionsConfig.groupedPermissions && Object.entries(permissionsConfig.groupedPermissions).map(([groupName, group]: [string, PermissionGroup]) => {
              const groupLabel = friendlyGroupLabels[groupName] || (group as any).label || groupName;
              const groupDesc = groupDescriptions[groupName] || '';
              const groupIcon = groupIcons[groupName] || <SecurityIcon />;
              const q = search.trim().toLowerCase();
              const perms = (group as any).permissions as PermissionDefinition[];
              const visible = !q
                ? perms
                : perms.filter(p =>
                    groupLabel.toLowerCase().includes(q) ||
                    (p.description || '').toLowerCase().includes(q) ||
                    p.id.toLowerCase().includes(q)
                  );
              if (!visible || visible.length === 0) return null;
              const allIds = visible.map(p => p.id);
              const selectedInGroup = allIds.filter(id => formData.permissions.includes(id)).length;
              
              return (
                <Accordion key={groupName} defaultExpanded={selectedInGroup > 0}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                      <Box sx={{ color: '#072f53' }}>{groupIcon}</Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {groupLabel}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {groupDesc}
                        </Typography>
                      </Box>
                      <Chip 
                        label={`${selectedInGroup}/${allIds.length}`} 
                        size="small" 
                        color={selectedInGroup > 0 ? 'primary' : 'default'}
                        sx={{ mr: 1 }}
                      />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2, gap: 1 }}>
                      <Button 
                        size="small" 
                        variant="outlined"
                        onClick={() => toggleGroup(allIds, true)} 
                        disabled={loading || isAdminAllSelected}
                      >
                        Välj alla
                      </Button>
                      <Button 
                        size="small" 
                        variant="outlined"
                        onClick={() => toggleGroup(allIds, false)} 
                        disabled={loading || isAdminAllSelected}
                      >
                        Avmarkera
                      </Button>
                    </Box>
                    <FormGroup>
                      {visible.map((p: PermissionDefinition) => (
                        <FormControlLabel
                          key={p.id}
                          control={
                            <Checkbox
                              checked={formData.permissions.includes(p.id)}
                              onChange={(e) => handlePermissionChange(p.id, e.target.checked)}
                              disabled={loading || isAdminAllSelected}
                              size="small"
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body2">
                                {friendlyPermissionLabels[(p.description || '').toUpperCase()] || p.description || p.id}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {p.id}
                              </Typography>
                            </Box>
                          }
                          sx={{ mb: 0.5 }}
                        />
                      ))}
                    </FormGroup>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Box>
        )}
        {permissionsConfig?.allPermissions && permissionsConfig.allPermissions.length === 0 && (
            <Typography>Inga behörigheter är definierade i systemet.</Typography>
        )}

      </DialogContent>
      <DialogActions sx={{ p: '16px 24px' }}>
        <Button onClick={onClose} disabled={loading} color="secondary">Avbryt</Button>
        <Button onClick={handleSubmit} disabled={loading || !formData.name.trim()} variant="contained">
          {loading ? <CircularProgress size={24} /> : (role ? 'Spara Ändringar' : 'Skapa Roll')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RoleFormModal; 